// Ported from /home/lk/website/dashboard/src/dashboard.jsx:262-278 (status colour list) and 4703-4764 (status change handler). India hooks removed: old backend endpoints, legacy owner-write call, activity-log call, client-side retry enqueue, messaging-app and EV-only statuses.
/**
 * US lead dispositions. CONTRACT REQUEST for P1: move this list to shared/contracts.mjs as `LEAD_DISPOSITIONS`
 * (P2 §2b) so the API validates the same keys. Until then this file is the client copy.
 *
 * `lease` says what the server does to the claim when the disposition is saved: renew it (keep working the lead),
 * release it (lead closed). `aiFollowUp` marks the dispositions for which the SERVER (P4) may schedule an AI
 * follow-up call; the client never schedules or places retries itself (S0.4).
 */
import type { Tone } from '@app/core/job_status';
import type { LeadRow } from './provider';

export interface Disposition {
  key: string; label: string; tone: Tone;
  /** false = set by the system only (AI receptionist, job creation); never offered in the select. */
  manual: boolean;
  lease: 'renew' | 'release';
  /** Ask for a follow-up time right after saving. */
  followUp?: boolean;
  aiFollowUp?: boolean;
}

export const LEAD_DISPOSITIONS: readonly Disposition[] = [
  { key: 'booked', label: 'Booked', tone: 'done', manual: false, lease: 'release' },
  { key: 'no_answer', label: 'No answer', tone: 'neutral', manual: true, lease: 'renew', aiFollowUp: true },
  { key: 'callback', label: 'Call back later', tone: 'transit', manual: true, lease: 'renew', followUp: true },
  { key: 'busy', label: 'Customer busy', tone: 'transit', manual: true, lease: 'renew', aiFollowUp: true },
  { key: 'voicemail', label: 'Left voicemail', tone: 'neutral', manual: true, lease: 'renew', aiFollowUp: true },
  { key: 'unavailable', label: 'No slot right now', tone: 'transit', manual: true, lease: 'renew', followUp: true },
  { key: 'phone_off', label: 'Phone off / unreachable', tone: 'neutral', manual: true, lease: 'renew' },
  { key: 'not_interested', label: 'Not interested', tone: 'neutral', manual: true, lease: 'release' },
  { key: 'out_of_area', label: 'Outside service area', tone: 'neutral', manual: true, lease: 'release' },
  { key: 'invalid_number', label: 'Invalid number', tone: 'danger', manual: true, lease: 'release' },
  { key: 'do_not_contact', label: 'Do not contact', tone: 'danger', manual: true, lease: 'release' },
  { key: 'spam', label: 'Spam', tone: 'neutral', manual: true, lease: 'release' },
  { key: 'vendor', label: 'Vendor / sales call', tone: 'neutral', manual: true, lease: 'release' },
  { key: 'status_inquiry', label: 'Job status question', tone: 'ai', manual: false, lease: 'release' },
  { key: 'info_only', label: 'Info only', tone: 'ai', manual: false, lease: 'release' },
  { key: 'transfer_missed', label: 'Transfer missed', tone: 'danger', manual: false, lease: 'renew' },
];

const BY_KEY = new Map(LEAD_DISPOSITIONS.map((d) => [d.key, d]));
export const dispositionOf = (key: string | null | undefined) => (key ? BY_KEY.get(key) ?? null : null);
export const MANUAL_DISPOSITIONS = LEAD_DISPOSITIONS.filter((d) => d.manual);

/**
 * The India change-handler guard, as a pure check. A booked lead is locked. Otherwise the change is allowed when the
 * lead is unclaimed (the server auto-claims it for me), mine, or I'm an owner. The server re-checks all of it.
 */
export function canChangeDisposition(lead: LeadRow, meUserId: string, isOwner: boolean): { ok: true } | { ok: false; reason: string } {
  if (lead.status === 'booked' || lead.job_id) return { ok: false, reason: 'Booked leads are closed.' };
  if (!lead.claimed_by || lead.claimed_by === meUserId || isOwner) return { ok: true };
  return { ok: false, reason: `${lead.claimed_by_name ?? 'Someone else'} is working this lead.` };
}
